import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, ButtonGroup, Button, ThemeProvider, Tabs } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import PieChartIcon from '@mui/icons-material/PieChart';
import GridViewIcon from '@mui/icons-material/GridView';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';

import useResultData from './Hooks/useResultData';
import useMapData from './Hooks/useMapData';
import SimulationHistorySelect from './Components/SimulationHistorySelect';
import SuggestionSwitch from './Components/SuggestionSwitch';
import SectionKeyNumber from './Sections/SectionKeyNumber';
import SectionCrops from './Sections/SectionCrops';
import SectionParcels from './Sections/SectionParcels';
import SectionParameters from './Sections/SectionParameters';
import usePdfData from './Hooks/usePdfData';
import { ExploitationContext, ModalContext } from 'utils/context';
import { handleDownloadPdf } from './Hooks/usePdfExport';
import useAUTH from 'providers/Auth/useAuth';
import { ReportModal } from 'components/generics/Modals/reportModal';
import { toast } from 'react-toastify';
import { displayErrorMessage, displayMessage } from 'utils/tools_functions';
import { getAllTechnicianFull } from 'services/API/Cooperative';
import { ROLE_ADMIN, ROLE_TECHNICIAN } from 'utils/const';
import { minimalRoleRequired } from 'utils';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { sendReport } from 'services/API/Simulation';
import CloseIcon from '@mui/icons-material/Close';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './result.style.scss';

const Result = ({ exploitation, setExploitation, culturesFromParent, simulationResult }) => {
  const { t, i18n } = useTranslation();

  const SECTIONS = useMemo(
    () => [
      { id: 'cultures', icon: PieChartIcon, label: t('common.cultures') },
      { id: 'chiffres', icon: InsightsIcon, label: t('simulation.result.navigation.keyNumbers') },
      { id: 'parcelles', icon: GridViewIcon, label: t('common.parcels') },
      { id: 'params', icon: TuneIcon, label: t('simulation.result.navigation.parameters') },
    ],
    [t],
  );

  const STRATEGY_LABELS = useMemo(
    () => ({
      MAX_GM: t('simulation.common.freeOptimization'),
      HISTORY: t('simulation.common.historyOptimization'),
    }),
    [t],
  );

  const getSuggestionEntries = useMemo(
    () => (suggestions) => {
      if (!suggestions) return [];
      return Object.entries(suggestions).map(([key, value], index) => ({
        key,
        label: STRATEGY_LABELS[value?.strategy] || value?.strategy || `Suggestion ${index + 1}`,
      }));
    },
    [STRATEGY_LABELS],
  );

  const [activeSection, setActiveSection] = useState('cultures');
  const [isParamsOpen, setIsParamsOpen] = useState(false);
  const [currentSuggestionKey, setCurrentSuggestionKey] = useState(null);
  const [currentPieIndex, setCurrentPieIndex] = useState(0);
  const [forcedChartMode, setForcedChartMode] = useState(null);
  const [stackedOpen, setStackedOpen] = useState({ cultures: false, intercultures: false });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const splitAnchorRef = useRef(null);

  const [showModal, updateShowModal] = useState(false);
  const [confirmSendReport, setConfirmSendReport] = useState(false);
  const [listMailUserFromModal, setListMailUserFromModal] = useState([]);
  const [techniciansAddressMailList, setTechniciansAddressMailList] = useState([]);

  const { user } = useAUTH();
  const { buildPdfData, downloadPdf } = usePdfData();
  const { currentSector, currentCooperative, culturesColors } = useContext(ExploitationContext);

  // Keep the original sort for the some nitrogen Array of indexes send by the algo
  const originalCultureSort = useRef([...simulationResult?.result?.cultures] ?? [])?.current;
  const [warningBannerDismissed, setWarningBannerDismissed] = useState(false);

  const resultData = useResultData(exploitation, setExploitation, simulationResult);
  const suggestions = simulationResult?.result?.suggestions;
  const suggestionEntries = useMemo(
    () => getSuggestionEntries(suggestions),
    [suggestions, getSuggestionEntries],
  );

  const effectiveSuggestionKey = useMemo(() => {
    if (!suggestionEntries.length) return null;
    if (currentSuggestionKey && suggestionEntries.some((e) => e.key === currentSuggestionKey)) {
      return currentSuggestionKey;
    }
    return suggestionEntries[0].key;
  }, [suggestionEntries, currentSuggestionKey]);

  const handleSuggestionChange = (key) => {
    const currentSuggest = resultData.suggest;
    resultData.setResult((prev) => ({
      ...prev,
      suggestions: {
        ...prev.suggestions,
        [effectiveSuggestionKey]: currentSuggest,
      },
    }));
    setCurrentSuggestionKey(key);
    resultData.setSuggest(resultData.result.suggestions[key]);
  };

  const mapData = useMapData(
    resultData.suggest,
    resultData.setSuggest,
    resultData.exploitationParcels,
    resultData.forecastCampaign,
    resultData.setHasChange,
  );

  const sectionRefs = {
    cultures: useRef(null),
    chiffres: useRef(null),
    parcelles: useRef(null),
    params: useRef(null),
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.dataset.section);
          }
        });
      },
      { rootMargin: '-70% 0px -20% 0px', threshold: 0 },
    );

    SECTIONS.forEach(({ id }) => {
      const el = sectionRefs[id]?.current;
      if (el) {
        el.dataset.section = id;
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = sectionRefs[id]?.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 140;
    window.scrollTo({ top });
  };

  const pdfArgs = {
    resultData,
    effectiveSuggestionKey,
    exploitation,
    user,
    sector: currentSector,
    cooperative: currentCooperative,
    culturesColors,
    mapData,
    buildPdfData,
    downloadPdf,
    setPdfLoading,
    setCurrentPieIndex,
    setForcedChartMode,
    setStackedOpen,
  };

  const sendPdfReportMail = async () => {
    const pdf = await handleDownloadPdf({ ...pdfArgs, skipSave: true });
    if (!pdf) return;

    const formData = new FormData();
    formData.append('pdf', pdf.output('blob'), 'simulation.pdf');
    formData.append('pdfName', `Assolement_${exploitation?.name}`);
    for (const mail of listMailUserFromModal) {
      formData.append('mail[]', mail);
    }
    formData.append('exploitationID', exploitation.id);

    sendReport(i18n.language, formData)
      .then(() => toast.success(displayMessage('SEND_REPORT_MAIL_OK')))
      .catch(() => toast.error(displayErrorMessage('ERR_SEND_REPORT_SIMULATION')));
  };

  useEffect(() => {
    if (confirmSendReport) {
      sendPdfReportMail();
      setConfirmSendReport(false);
    }
  }, [confirmSendReport]);

  const handleSendSimulation = () => {
    setSplitOpen(false);
    if (!showModal) {
      let options = null;
      if (minimalRoleRequired(user.role, ROLE_ADMIN)) {
        options = {
          queryParamsString: { cooperative: localStorage.getItem('cooperative') },
        };
      }
      getAllTechnicianFull(options)
        .then((res) => {
          const users = res.data.data;
          const techniciansSector = [];
          for (const u of users) {
            if (u.roles.includes('ROLE_TECHNICIAN')) {
              const technicianSector = u.sectors.filter(
                (sector) => sector?.id === exploitation.sector.id,
              );
              if (technicianSector.length > 0) {
                techniciansSector.push(u.email);
              }
            }
          }
          setTechniciansAddressMailList([...techniciansSector]);
          updateShowModal(true);
        })
        .catch((error) => {
          console.log(error);
          toast.error(displayErrorMessage('ERR_GET_TECHNICIAN_FULL'));
        });
    } else {
      updateShowModal(false);
    }
  };

  const toggleModal = handleSendSimulation;

  const WarningBanner = () => {
    const warnings = simulationResult?.result?.warnings;
    if (!warnings?.length || warningBannerDismissed) return null;

    warnings.forEach((warning) => {
      warning.softened.map((softValues) => {
        for (const key in softValues.additionalInfo) {
          const value = softValues.additionalInfo[key];
          const sourceValue = value?.min || value?.max;
          const typeKey = value?.min ? 'min' : value?.max ? 'max' : null;
          if (typeof value === 'object' && sourceValue) {
            Object.assign(softValues.additionalInfo, sourceValue);
            if (typeKey) {
              softValues.additionalInfo.value = typeKey;
            }
          }
        }
      });
    });

    return (
      <div className="simulation-warning-banner">
        <div className="warningBannerRow">
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <strong>{displayErrorMessage('3000_WARNING')}</strong>
            </AccordionSummary>
            <AccordionDetails>
              <div className="warningBannerTitle">{displayErrorMessage('3000_TITLE')}</div>

              {warnings.map((warning, warningIndex) => (
                <Accordion
                  key={warning.campaign}
                  className="warningCampaignAccordion"
                  disableGutters
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <h3>{displayErrorMessage('3000_CAMPAIGN', { year: warning.campaign })}</h3>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div key={warningIndex} className="warningEntry">
                      {warning.softened.map((softened, index) => (
                        <span key={`softened-${index}`} className="message">
                          {displayErrorMessage(softened.error_code, softened.additionalInfo)}
                        </span>
                      ))}
                      <span className="causeLabel">{displayErrorMessage('3000_CAUSE')}</span>
                      {warning.cause.map((cause, index) => (
                        <span key={`cause-${index}`} className="causeMessage">
                          {displayErrorMessage(cause.error_code, cause.additionalInfo)}
                        </span>
                      ))}
                    </div>
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>

          <button
            className="warningBannerClose"
            onClick={() => setWarningBannerDismissed(true)}
            aria-label={t('common.close')}
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="result">
      <nav className="sidebar">
        <Tabs
          orientation="vertical"
          value={activeSection}
          onChange={(_event, id) => scrollToSection(id)}
          className="sections"
        >
          {SECTIONS.map((section) => (
            <Tab
              key={section.id}
              value={section.id}
              icon={<section.icon />}
              label={section.label}
              className="tab"
            />
          ))}
        </Tabs>
      </nav>

      <div className="container">
        <WarningBanner />
        <div className="header">
          {suggestionEntries.length > 1 && (
            <SuggestionSwitch
              entries={suggestionEntries}
              currentKey={effectiveSuggestionKey}
              onChange={handleSuggestionChange}
            />
          )}

          <ThemeProvider theme={customButtonTheme}>
            <ModalContext.Provider
              value={{ showModal, toggleModal, setConfirmSendReport, setListMailUserFromModal }}
            >
              <ButtonGroup
                ref={splitAnchorRef}
                variant="contained"
                disabled={pdfLoading}
                className="pdf-btn-group"
              >
                <Button
                  variant="contained"
                  className="pdf-btn"
                  onClick={() => handleDownloadPdf(pdfArgs)}
                >
                  {pdfLoading
                    ? t('simulation.result.buttons.generating')
                    : t('simulation.result.buttons.downloadPdf')}
                </Button>

                {minimalRoleRequired(user.role, ROLE_TECHNICIAN) && (
                  <Button
                    className="pdf-btn-arrow"
                    size="small"
                    onClick={() => setSplitOpen((prev) => !prev)}
                    aria-controls={splitOpen ? 'split-button-menu' : undefined}
                    aria-expanded={splitOpen ? 'true' : undefined}
                    aria-haspopup="menu"
                  >
                    <ArrowDropDownIcon />
                  </Button>
                )}
              </ButtonGroup>

              <Popper
                sx={{ zIndex: 1 }}
                open={splitOpen}
                anchorEl={splitAnchorRef.current}
                role={undefined}
                transition
                disablePortal
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={() => setSplitOpen(false)}>
                        <MenuList id="split-button-menu" autoFocusItem>
                          <MenuItem onClick={handleSendSimulation}>
                            {t('simulation.result.buttons.sendSimulation')}
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>

              {showModal && <ReportModal techniciansAddressMailList={techniciansAddressMailList} />}
            </ModalContext.Provider>
          </ThemeProvider>

          <SimulationHistorySelect
            simulationHistory={resultData.simulationHistory}
            selectedSimulationId={resultData.selectedSimulationId}
            onSelectSimulation={resultData?.handleSelectSimulation}
            removeSimulationHistory={resultData?.removeSimulationHistory}
          />
        </div>

        <div className="scroll-content">
          <section ref={sectionRefs.cultures} className="section" id="section-cultures">
            {resultData.suggest && (
              <SectionCrops
                pieChartData={resultData.pieChartData}
                interculturePieChartData={resultData.interculturePieChartData}
                forecastCampaign={resultData.forecastCampaign}
                cultures={resultData.result.cultures}
                totalExploitationSurface={resultData.totalExploitationSurface}
                currentPieIndex={currentPieIndex}
                setCurrentPieIndex={setCurrentPieIndex}
                forcedChartMode={forcedChartMode}
                stackedOpen={stackedOpen}
                setStackedOpen={setStackedOpen}
              />
            )}
          </section>
          <section ref={sectionRefs.chiffres} className="section" id="section-chiffres">
            {resultData.suggest && (
              <SectionKeyNumber
                result={resultData.result}
                previous={resultData.result?.previous}
                forecastCampaign={resultData.forecastCampaign}
                suggest={resultData.suggest}
                grossMarginTableData={resultData.grossMarginTableData}
                lineChartData={resultData.lineChartData}
                configConstraints={resultData.currentSimulationResult?.config?.constraints}
                cultures={originalCultureSort}
                capHelps={resultData.result?.capHelps}
              />
            )}
          </section>
          <section ref={sectionRefs.parcelles} className="section" id="section-parcelles">
            {resultData.suggest && (
              <SectionParcels
                pieChartData={resultData.pieChartData}
                forecastCampaign={resultData.forecastCampaign}
                suggest={resultData.suggest}
                setSuggest={resultData.setSuggest}
                result={resultData.result}
                exploitationParcels={resultData.exploitationParcels}
                coordsExist={resultData.coordsExist}
                mapData={mapData}
                editedSynthesisCellsRef={resultData.editedSynthesisCellsRef}
                launchCalculette={resultData.launchCalculette}
                hasChange={resultData.hasChange}
                setHasChange={resultData.setHasChange}
                configConstraints={resultData.currentSimulationResult?.config?.constraints}
              />
            )}
          </section>
          <section ref={sectionRefs.params} className="section" id="section-params">
            <SectionParameters
              currentSimulationResult={resultData.currentSimulationResult}
              exploitation={exploitation}
              isOpen={isParamsOpen}
              onToggle={() => setIsParamsOpen((prev) => !prev)}
            />
          </section>
          {resultData?.currentSimulationResult?.jobId && (
            <h6>
              {t('simulation.common.jobId')} : {resultData.currentSimulationResult.jobId}
            </h6>
          )}
        </div>
      </div>
    </div>
  );
};

export default Result;
